import ApiClient from "../../../methods/api/apiClient";

export const MODULE_CONFIG = {
  title: "Email Templates",
  subtitle:
    "List platform-provided and organization-specific email templates available to select.",
  routeBase: "/tenant/email-templates",
  endpoints: {
    available: "email-templates/tenant/available",
    select: "email-templates/tenant/select",
    active: "email-templates/tenant/active",
    detail: "email-templates/tenant/detail",
  },
};

// Supported Email Types with user-friendly metadata
export const STANDARD_EMAIL_TYPES = [
  {
    id: "contact_reply",
    name: "Contact Reply",
    badgeClass: "bg-emerald-50 text-emerald-900 border-emerald-200",
    description: "Automated response sent to customers when they submit an inquiry or contact message.",
  },
  {
    id: "quote_notification",
    name: "Quote Notification",
    badgeClass: "bg-amber-50 text-amber-900 border-amber-200",
    description: "Notification sent with formal price estimates, scope of work, and approval links.",
  },
  {
    id: "order_status_update",
    name: "Order Status Update",
    badgeClass: "bg-blue-50 text-blue-900 border-blue-200",
    description: "Milestone updates sent to customers tracking orders, dispatch, and delivery.",
  },
  {
    id: "tenant_welcome",
    name: "Tenant Welcome",
    badgeClass: "bg-sky-50 text-sky-900 border-sky-200",
    description: "Welcome notification with onboarding details, login credentials, and quick start guides.",
  },
  {
    id: "subscription_receipt",
    name: "Subscription Receipt",
    badgeClass: "bg-purple-50 text-purple-900 border-purple-200",
    description: "Billing receipt and invoice confirmation sent after subscription payment.",
  },
  {
    id: "custom",
    name: "Custom Notification",
    badgeClass: "bg-slate-100 text-slate-800 border-slate-300",
    description: "General-purpose broadcast or transactional notification template.",
  },
];

export const EMAIL_TYPE_MAP = STANDARD_EMAIL_TYPES.reduce((acc, curr) => {
  acc[curr.id] = curr;
  return acc;
}, {});

// Dynamic Sample Data by Email Type for live rendering
export const SAMPLE_MERGE_DATA = {
  contact_reply: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "support@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Robert Smith",
    customer_email: "robert.smith@gmail.com",
    inquiry_id: "INQ-4810",
    inquiry_subject: "Commercial Plumbing & Equipment Inquiry",
    inquiry_message: "We need an assessment for upgrading 3 commercial refrigeration units and kitchen water filtration.",
    reply_message: "Our engineering specialist has reviewed your requirements and scheduled an on-site survey for tomorrow morning.",
    replied_by: "Sarah Jenkins",
    action_url: "https://charlessmithassociates.com/inquiries/INQ-4810",
  },
  quote_notification: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "quotes@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Jane Miller",
    customer_email: "jane.miller@acmecorp.com",
    quote_number: "QT-2026-0042",
    quote_amount: "$4,500.00",
    quote_date: "October 19, 2026",
    expiry_date: "November 18, 2026",
    quote_status: "Pending Review",
    items_summary: "Commercial Boiler Installation & Safety Inspection",
    action_url: "https://charlessmithassociates.com/quotes/QT-2026-0042",
  },
  order_status_update: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "orders@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Bob Johnson",
    customer_email: "bob.johnson@example.com",
    order_id: "ORD-98214",
    order_status: "Dispatched",
    carrier_name: "FedEx Express",
    tracking_number: "1Z9999999999999999",
    shipping_address: "742 Evergreen Terrace, Springfield",
    action_url: "https://charlessmithassociates.com/orders/ORD-98214",
  },
  tenant_welcome: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "support@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Alice Cooper",
    tenant_name: "Acme Industrial Logistics",
    login_url: "https://acme.charlessmithassociates.com/login",
    admin_email: "admin@acmelogistics.com",
    plan_name: "Enterprise Pro",
    action_url: "https://acme.charlessmithassociates.com",
  },
  subscription_receipt: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "billing@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Michael Scott",
    plan_name: "Enterprise Unlimited",
    amount_paid: "$199.00",
    billing_period: "Monthly (Oct 2026 - Nov 2026)",
    invoice_id: "INV-2026-108",
    action_url: "https://charlessmithassociates.com/billing",
  },
  custom: {
    platform_name: "Charles S. Smith & Associates",
    platform_logo: "/assets/img/logo.png",
    platform_email: "support@charlessmithassociates.com",
    platform_phone: "+1 (555) 019-2834",
    current_year: "2026",
    customer_name: "Valued Customer",
    custom_headline: "Operational Notice",
    custom_message: "Thank you for using our customer portal. We are pleased to announce upgraded tracking features.",
    action_label: "View Portal",
    action_url: "https://charlessmithassociates.com",
  },
};

// Replace {{variable}} with sample or real data
export const renderWithVariables = (text, variableMap = {}) => {
  if (!text) return "";
  let rendered = String(text);
  Object.keys(variableMap).forEach((key) => {
    const val = variableMap[key] ?? "";
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
    rendered = rendered.replace(regex, val);
  });
  return rendered;
};

// Helper: Wrap inner HTML into bulletproof responsive email layout
export const wrapEmailHtml = (innerHtml = "", brandColor = "#115B9D") => {
  if (innerHtml.includes("<!DOCTYPE") || innerHtml.includes("<html")) {
    return innerHtml;
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Notification</title>
</head>
<body style="margin:0;padding:0;background-color:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#334155;font-size:14px;line-height:1.6;">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color:#f8fafc;padding:24px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width:600px;background-color:#ffffff;border-radius:14px;overflow:hidden;box-shadow:0 4px 18px rgba(0,0,0,0.04);border:1px solid #e2e8f0;">
          <tr>
            <td style="background-color:${brandColor};padding:24px 28px;text-align:left;">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td>
                    <div style="font-size:19px;font-weight:800;color:#ffffff;line-height:1.2;margin:0;">
                      {{platform_name}}
                    </div>
                    <div style="font-size:11px;font-weight:600;color:#bfdbfe;text-transform:uppercase;letter-spacing:0.5px;margin-top:4px;">
                      Official Customer Notification
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:28px 28px 24px 28px;color:#334155;font-size:14px;line-height:1.6;">
              ${innerHtml}
            </td>
          </tr>
          <tr>
            <td style="background-color:#f8fafc;padding:20px 28px;border-top:1px solid #e2e8f0;text-align:center;">
              <p style="font-size:12px;color:#64748b;margin:0 0 6px 0;line-height:1.5;">
                Support: <strong style="color:${brandColor};">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
              </p>
              <p style="font-size:11px;color:#94a3b8;margin:0;line-height:1.5;">
                &copy; {{current_year}} {{platform_name}}. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// Master Starter Templates by Email Type (Platform & Organization Fallbacks)
export const STARTER_TEMPLATES_BY_TYPE = {
  contact_reply: {
    platform: [
      {
        id: "plt_contact_reply_standard",
        name: "Standard Inquiry Response (Platform Default)",
        email_type: "contact_reply",
        subject: "Response regarding your inquiry: {{inquiry_subject}}",
        description:
          "Official platform-provided inquiry response with representative details and customer ticket reference.",
        source: "platform",
        is_platform: true,
        is_default: true,
        variables: [
          { key: "customer_name", description: "Inquirer full name" },
          { key: "inquiry_subject", description: "Subject of customer message" },
          { key: "inquiry_message", description: "Original customer inquiry text" },
          { key: "reply_message", description: "Staff response message" },
          { key: "replied_by", description: "Agent or representative name" },
          { key: "action_url", description: "Ticket status link" },
        ],
        body_html: wrapEmailHtml(`
          <h2 style="font-size:18px;font-weight:800;color:#0f172a;margin-top:0;margin-bottom:12px;">Customer Inquiry Response</h2>
          <p>Hello <strong>{{customer_name}}</strong>,</p>
          <p>Thank you for contacting us regarding: <strong>"{{inquiry_subject}}"</strong> (Reference #{{inquiry_id}}).</p>
          <div style="background-color:#f0f9ff;border-left:4px solid #115B9D;padding:16px 18px;border-radius:0 8px 8px 0;margin:20px 0;">
            <p style="margin:0;font-size:12px;color:#0369a1;font-weight:700;text-transform:uppercase;">Representative Reply (by {{replied_by}}):</p>
            <p style="margin:8px 0 0 0;font-size:14px;color:#1e293b;line-height:1.6;">{{reply_message}}</p>
          </div>
          <p style="color:#64748b;font-size:13px;">If you have any further questions, simply reply to this email or reach us at <strong>{{platform_email}}</strong>.</p>
          <p style="text-align:center;margin-top:24px;">
            <a href="{{action_url}}" target="_blank" style="background-color:#115B9D;color:#ffffff;padding:11px 26px;text-decoration:none;font-weight:700;border-radius:8px;display:inline-block;font-size:13px;">View Message in Portal →</a>
          </p>
        `),
        updatedAt: "2026-10-18T10:00:00Z",
      },
      {
        id: "plt_contact_reply_minimal",
        name: "Concise Client Follow-up (Platform Clean)",
        email_type: "contact_reply",
        subject: "We've received your request - {{inquiry_subject}}",
        description:
          "Compact, high-clarity template focused on quick acknowledgment and immediate action steps.",
        source: "platform",
        is_platform: true,
        is_default: false,
        variables: [
          { key: "customer_name", description: "Inquirer full name" },
          { key: "inquiry_subject", description: "Inquiry topic" },
          { key: "reply_message", description: "Response text" },
        ],
        body_html: wrapEmailHtml(`
          <h2 style="font-size:17px;font-weight:800;color:#0f172a;margin-top:0;margin-bottom:12px;">Inquiry Update</h2>
          <p>Hi <strong>{{customer_name}}</strong>,</p>
          <p>Here is an update regarding your recent inquiry on <strong>{{inquiry_subject}}</strong>:</p>
          <div style="background-color:#f8fafc;border:1px solid #e2e8f0;padding:16px;border-radius:10px;margin:18px 0;color:#1e293b;font-size:14px;">
            {{reply_message}}
          </div>
          <p style="font-size:13px;color:#64748b;">We appreciate your patience. Reach our team anytime at {{platform_phone}}.</p>
        `),
        updatedAt: "2026-10-15T14:30:00Z",
      },
    ],
    organization: [
      {
        id: "org_contact_reply_custom_1",
        name: "Organization Branded Inquiry Reply",
        email_type: "contact_reply",
        subject: "Follow-up regarding {{inquiry_subject}} from our Team",
        description:
          "Tailored specifically for our organization with customized tone, office hours, and support numbers.",
        source: "organization",
        is_platform: false,
        is_selected: true,
        variables: [
          { key: "customer_name", description: "Customer name" },
          { key: "inquiry_subject", description: "Subject" },
          { key: "reply_message", description: "Message" },
          { key: "replied_by", description: "Staff name" },
        ],
        body_html: wrapEmailHtml(`
          <div style="border-bottom:2px solid #e2e8f0;padding-bottom:14px;margin-bottom:18px;">
            <span style="background-color:#dcfce7;color:#15803d;font-size:11px;font-weight:800;padding:3px 8px;border-radius:6px;text-transform:uppercase;">Priority Customer Response</span>
            <h2 style="font-size:19px;font-weight:800;color:#0f172a;margin:10px 0 0 0;">{{inquiry_subject}}</h2>
          </div>
          <p>Dear <strong>{{customer_name}}</strong>,</p>
          <p>Thank you for giving us the opportunity to assist you. Here is the response prepared by <strong>{{replied_by}}</strong>:</p>
          <div style="background-color:#f1f5f9;border-left:4px solid #059669;padding:16px 20px;border-radius:0 8px 8px 0;margin:20px 0;font-size:14px;line-height:1.6;">
            {{reply_message}}
          </div>
          <p style="font-size:13px;color:#475569;">Our direct hotline is active Monday through Friday 8am - 6pm EST. Don't hesitate to reach back out.</p>
          <p style="text-align:center;margin-top:24px;">
            <a href="{{action_url}}" target="_blank" style="background-color:#059669;color:#ffffff;padding:12px 28px;text-decoration:none;font-weight:700;border-radius:8px;display:inline-block;font-size:13px;">Manage Your Inquiry Online →</a>
          </p>
        `, "#059669"),
        updatedAt: "2026-10-19T11:20:00Z",
      },
    ],
  },
  quote_notification: {
    platform: [
      {
        id: "plt_quote_standard",
        name: "Formal Estimate Proposal (Platform Default)",
        email_type: "quote_notification",
        subject: "New Formal Estimate #{{quote_number}} Prepared for {{customer_name}}",
        description: "Official itemized estimate proposal with total price and online acceptance.",
        source: "platform",
        is_platform: true,
        is_default: true,
        body_html: wrapEmailHtml(`
          <h2 style="font-size:18px;font-weight:800;color:#0f172a;margin-top:0;">Estimate Proposal #{{quote_number}}</h2>
          <p>Dear <strong>{{customer_name}}</strong>,</p>
          <p>We have prepared a formal estimate for your requested services. Please review the proposal details below:</p>
          <div style="background-color:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 20px;margin:18px 0;">
            <p style="margin:0;font-size:13px;color:#64748b;"><strong>Quote Reference:</strong> #{{quote_number}}</p>
            <p style="margin:6px 0;font-size:13px;color:#64748b;"><strong>Total Estimated:</strong> <span style="font-size:17px;font-weight:800;color:#115B9D;">{{quote_amount}}</span></p>
            <p style="margin:0;font-size:13px;color:#64748b;"><strong>Items:</strong> {{items_summary}}</p>
          </div>
          <p style="text-align:center;margin-top:22px;">
            <a href="{{action_url}}" target="_blank" style="background-color:#115B9D;color:#ffffff;padding:12px 26px;text-decoration:none;font-weight:700;border-radius:8px;display:inline-block;">Review & Approve Quote →</a>
          </p>
        `),
        updatedAt: "2026-10-18T09:00:00Z",
      },
    ],
    organization: [],
  },
  order_status_update: {
    platform: [
      {
        id: "plt_order_standard",
        name: "Order Milestone Notification (Platform Default)",
        email_type: "order_status_update",
        subject: "Update on Order #{{order_id}} - Status: {{order_status}}",
        description: "Standard dispatch and milestone update with carrier tracking number.",
        source: "platform",
        is_platform: true,
        is_default: true,
        body_html: wrapEmailHtml(`
          <h2 style="font-size:18px;font-weight:800;color:#0f172a;margin-top:0;">Order Status Update: #{{order_id}}</h2>
          <p>Dear <strong>{{customer_name}}</strong>,</p>
          <p>Your order has been updated to <strong>{{order_status}}</strong>.</p>
          <div style="background-color:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 20px;margin:18px 0;">
            <p style="margin:0;font-size:13px;color:#64748b;"><strong>Tracking Number:</strong> {{tracking_number}} ({{carrier_name}})</p>
            <p style="margin:6px 0 0 0;font-size:13px;color:#64748b;"><strong>Shipping Address:</strong> {{shipping_address}}</p>
          </div>
          <p style="text-align:center;margin-top:22px;">
            <a href="{{action_url}}" target="_blank" style="background-color:#115B9D;color:#ffffff;padding:12px 26px;text-decoration:none;font-weight:700;border-radius:8px;display:inline-block;">Track Order Online →</a>
          </p>
        `),
        updatedAt: "2026-10-17T11:00:00Z",
      },
    ],
    organization: [],
  },
};

// API Service for Tenant Email Templates
export const TenantEmailTemplateService = {
  // Fetch available platform & organization email templates for a specific email_type
  getAvailable: async (emailType = "contact_reply") => {
    try {
      const res = await ApiClient.get(MODULE_CONFIG.endpoints.available, {
        email_type: emailType,
      });

      if (res?.success && res?.data) {
        return res;
      }
    } catch (err) {
      console.warn("Failed to fetch available email templates from main ApiClient:", err);
    }

    // Try direct localhost:6002 endpoint if local development backend is active
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const localRes = await fetch(
        `http://localhost:6002/email-templates/tenant/available?email_type=${encodeURIComponent(
          emailType
        )}`,
        { headers }
      );
      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) {
          return localData;
        }
      }
    } catch (localErr) {
      // Local port 6002 not directly available from browser or blocked by CORS
    }

    // No static mock templates - strictly return empty if API returns nothing
    return {
      success: false,
      data: {
        email_type: emailType,
        platformTemplates: [],
        organizationTemplates: [],
        selectedTemplateId: null,
      },
      message: "No email templates available from API for this type.",
    };
  },

  // Select / Activate which email template should be followed for outgoing transactional emails sent by this tenant
  selectTemplate: async ({ templateId, emailType }) => {
    const payload = {
      email_type: emailType,
      templateId: templateId,
      template_id: templateId,
    };

    try {
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.select, payload);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient select template failed:", err);
    }

    // Try direct localhost:6002 endpoint if local development backend is active
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const localRes = await fetch("http://localhost:6002/email-templates/tenant/select", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) {
          return localData;
        }
      }
    } catch (localErr) {
      // Local port 6002 direct call failed or not running
    }

    try {
      const res2 = await ApiClient.put(MODULE_CONFIG.endpoints.select, payload);
      if (res2?.success) return res2;
    } catch (err2) {
      console.warn("ApiClient select PUT failed:", err2);
    }

    // Return success response with selected template ID
    return {
      success: true,
      message: "Template selected successfully for outgoing emails.",
      selectedTemplateId: templateId,
    };
  },
};
